<script setup lang="ts">
import { computed } from 'vue';
import { formatDateTime } from '@/api/datetime';
import { i18n, t } from '@/i18n';
import type { Game } from '@/api/types';

/*
 * When this game was made, when it kicks off, and when it wrapped up — in the reader's own zone.
 *
 * A component rather than markup repeated on two screens, and it takes the game as a prop so it
 * touches no store, which is what apps/web/docs/ARCHITECTURE.md asks of anything under
 * components/.
 *
 * It lives at the foot of both game screens on purpose: this is provenance, not something anybody
 * acts on. Above the fold it would compete with the grid, which is the thing people came for.
 */

const props = defineProps<{ game: Game }>();

interface Stamp {
  label: string;
  /** The exact instant, for the `datetime` attribute. */
  iso: string;
  /** The same instant, local and readable. */
  text: string;
}

/**
 * `lockedAt` is deliberately not here. It exists as the seed for the free-square deal rather than
 * as something to read, and a fourth timestamp turns a quiet line into a table.
 */
const stamps = computed<Stamp[]>(() => {
  const out: Stamp[] = [];

  const add = (label: string, iso: string | null | undefined): void => {
    // The chosen language, not the browser's: somebody reading the app in Afrikaans should not
    // get an English date in the middle of it. `formatDateTime` takes it as a parameter for
    // exactly this reason — see src/api/datetime.ts.
    const text = formatDateTime(iso, i18n.global.locale.value);
    // Only when it parses: the values come off the wire, and "Invalid Date" on a screen is worse
    // than the line not being there.
    if (iso && text) out.push({ label, iso, text });
  };

  add(t('timestamps.created'), props.game.createdAt);
  add(t('timestamps.kickOff'), props.game.kickOffAt);
  // One stored timestamp, two endings — telling somebody a called-off game "finished" would be a
  // small lie the status makes unnecessary.
  add(
    props.game.status === 'CANCELLED' ? t('timestamps.cancelled') : t('timestamps.finished'),
    props.game.endedAt,
  );

  return out;
});
</script>

<template>
  <p v-if="stamps.length > 0" class="text-xs text-ink-muted">
    <template v-for="(stamp, index) in stamps" :key="stamp.label">
      <span v-if="index > 0"> · </span>{{ stamp.label }}
      <time :datetime="stamp.iso">{{ stamp.text }}</time>
    </template>
  </p>
</template>
